/**
 * TradeScene — a warm first trade with a regular (CLAUDE.md signature mechanic #4).
 *
 * The flow, kept generous and unhurried (no haggling, no stress, never a wrong
 * choice):
 *   1. A regular you've met greets you and offers a Pokémon you don't have yet —
 *      great for the living dex. We deliberately prefer a *trade-evolution* species
 *      (e.g. Haunter) when the dataset has one, so the juicy trade-evo moment fires
 *      end-to-end the very first time.
 *   2. You pick one of your team mons to send their way (a sensible one is
 *      pre-selected — one tap and you're done, or just trust the default).
 *   3. The two sprites cross the screen, and if the received mon evolves *on the
 *      trade* (core/evolution's `trade` trigger), it does so with a gentle glow and
 *      a happy line. The dex, your team, and the regular's friendship all update.
 *
 * The renderer owns NO rules: the trade-evolution canon is `checkEvolution` /
 * `evolveInstance` from `@/core`; persistence (team / box / dex / society) is folded
 * back through the GameSession. A "Maybe later" exit always costs nothing.
 *
 * Launch contract (full-screen scene, returns to Town):
 *   SceneFlow.goTo(this, 'Trade', { data: { trainerId?: string } });
 * `trainerId` names the approached regular; absent ⇒ we pick a friendly default so
 * the scene is always reachable and self-contained.
 *
 * Responsive + touch-first throughout (Layout + the cozy UI kit): the two offer
 * panels sit side-by-side on a wide screen and stack into a column on a phone, and
 * every target is a big, soft tap.
 */
import Phaser from 'phaser';
import type { PokemonInstance, SpeciesData, MoveData, MoveSlot, SimTrainer } from '@/shared';
import { checkEvolution, evolveInstance, computeStats, getNature, expForLevel } from '@/core';
import type { GameSession } from '@/session';
import type { DataIndex } from '@/data';
import { FIXTURE_SPECIES } from '@/data';
import { COLORS, CSS, FONT, SPACING } from './ui/theme.js';
import { Panel } from './ui/Panel.js';
import { Button } from './ui/Button.js';
import { DialogBox } from './ui/DialogBox.js';
import { ScrollList } from './ui/ScrollList.js';
import { Toast } from './ui/Toast.js';
import { Layout } from './layout/index.js';
import { SpriteResolver } from './sprites/index.js';
import { fadeIn, goTo, isReducedMotion } from './flow.js';
import { REGISTRY_DATA_INDEX, REGISTRY_SPRITES } from './PreloadScene.js';
import { getSession } from './session.js';

/** Data the scene may be launched with (all optional — it self-resolves a default). */
interface TradeLaunchData {
  /** The approached regular's SimTrainer id. */
  readonly trainerId?: string;
}

/** A tappable card for one of the player's team mons (the "you offer" side). */
interface OfferCard {
  readonly mon: PokemonInstance;
  readonly container: Phaser.GameObjects.Container;
  readonly panel: Panel;
  readonly sprite: Phaser.GameObjects.Image;
  readonly check: Phaser.GameObjects.Text;
}

const CARD_W = 116;
const CARD_H = 138;
const OFFER_ART = 96;

export class TradeScene extends Phaser.Scene {
  private layout!: Layout;
  private index!: DataIndex;
  private resolver!: SpriteResolver;
  private session!: GameSession;
  private reduced = false;
  private busy = false;
  private committed = false;

  // Resolved trade subjects.
  private partner!: SimTrainer;
  private offeredSpecies!: SpeciesData;
  private playerTeam: PokemonInstance[] = [];
  private chosenOfferId: string | null = null;

  // ── UI handles ────────────────────────────────────────────────────────────────
  private backdrop?: Phaser.GameObjects.Graphics;
  private heading?: Phaser.GameObjects.Text;
  private theirPanel?: Panel;
  private theirSprite?: Phaser.GameObjects.Image;
  private theirName?: Phaser.GameObjects.Text;
  private theirNote?: Phaser.GameObjects.Text;
  /** Where the offered sprite + glow sit, stored so async loads place themselves. */
  private theirSpriteAnchor = { x: 0, y: 0 };
  private yourLabel?: Phaser.GameObjects.Text;
  private offerCards: OfferCard[] = [];
  /**
   * On a phone the "send one of yours" cards stack into a column that, with a full team,
   * is taller than the band between the team label and the bottom-pinned Trade!/Maybe
   * later buttons + greeting dialog — so they go in a touch-drag ScrollList there. On a
   * wide screen the cards fit beside the offer and are placed directly (list unused).
   */
  private offerScroll?: ScrollList;
  private confirmButton?: Button;
  private laterButton?: Button;
  private dialog?: DialogBox;
  private glow?: Phaser.GameObjects.Image;

  constructor() {
    super('Trade');
  }

  create(data?: TradeLaunchData): void {
    // Phaser REUSES this scene instance, so create() re-runs on every RE-ENTRY (Town →
    // Trade → Town → Trade). Instance fields survive: the offerCards/playerTeam arrays
    // would otherwise grow each visit (doubled, dead cards) and the busy/committed guards
    // would stay latched (a mid-trade lock that never released). resetState() clears them.
    this.resetState();

    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.reduced = isReducedMotion();
    this.layout = Layout.attach(this);
    this.session = getSession(this);
    this.index =
      (this.registry.get(REGISTRY_DATA_INDEX) as DataIndex | undefined) ?? fixtureIndex();
    this.resolver =
      (this.registry.get(REGISTRY_SPRITES) as SpriteResolver | undefined) ??
      new SpriteResolver(this);

    this.drawBackdrop();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());

    const resolved = this.resolveTrade(data?.trainerId);
    if (!resolved) {
      // Nothing to trade right now (e.g. the player already has everything a regular
      // can spare). Bow out warmly rather than show an empty screen.
      this.showNothingToTrade();
      this.layout.on('change', () => this.relayout());
      fadeIn(this);
      return;
    }

    this.partner = resolved.partner;
    this.offeredSpecies = resolved.species;
    this.playerTeam = this.session.getTeam().map((m) => m);
    this.chosenOfferId = this.pickDefaultOffer();

    this.buildUi();
    this.relayout();
    this.layout.on('change', () => this.relayout());
    fadeIn(this);
  }

  /**
   * Drop every reference a prior visit's create() populated so re-entry starts clean.
   * Display objects were torn down by the previous SHUTDOWN (teardown()); this clears the
   * arrays/flags so nothing stacks and the trade guards release. Safe on a first entry.
   */
  private resetState(): void {
    this.playerTeam = [];
    this.offerCards = [];
    this.offerScroll = undefined;
    this.chosenOfferId = null;
    this.busy = false;
    this.committed = false;
    this.backdrop = undefined;
    this.heading = undefined;
    this.theirPanel = undefined;
    this.theirSprite = undefined;
    this.theirName = undefined;
    this.theirNote = undefined;
    this.yourLabel = undefined;
    this.confirmButton = undefined;
    this.laterButton = undefined;
    this.dialog = undefined;
    this.glow = undefined;
  }

  /**
   * SHUTDOWN handler — Phaser reuses this scene instance, so leave nothing behind a
   * re-entry would trip over: kill tweens/timers and destroy every card + UI handle
   * (Buttons/Panels/DialogBox each clean up their own scene-input listeners on destroy).
   */
  private teardown(): void {
    this.tweens.killAll();
    this.time.removeAllEvents();
    for (const c of this.offerCards) c.container.destroy();
    this.offerCards = [];
    this.offerScroll?.destroy();
    this.offerScroll = undefined;
    this.backdrop?.destroy();
    this.heading?.destroy();
    this.theirPanel?.destroy();
    this.theirSprite?.destroy();
    this.theirName?.destroy();
    this.theirNote?.destroy();
    this.yourLabel?.destroy();
    this.confirmButton?.destroy();
    this.laterButton?.destroy();
    this.dialog?.destroy();
    this.glow?.destroy();
    this.backdrop = undefined;
    this.heading = undefined;
    this.theirPanel = undefined;
    this.theirSprite = undefined;
    this.theirName = undefined;
    this.theirNote = undefined;
    this.yourLabel = undefined;
    this.confirmButton = undefined;
    this.laterButton = undefined;
    this.dialog = undefined;
    this.glow = undefined;
  }

  // ── calm placeholder environment (programmatic; swappable for real art) ───────

  /**
   * A soft, cozy "trade nook" backdrop drawn entirely with Graphics — no tilesets
   * downloaded. A gentle dusk wash up top and a warm floor band below, with a faint
   * rounded "counter" the two sides meet across. Redrawn on resize. Trivially
   * swappable for real tilemap art later (it owns no layout, just paints the safe rect).
   */
  private drawBackdrop(): void {
    const g = this.backdrop ?? this.add.graphics();
    this.backdrop = g;
    g.setDepth(-10);
    g.clear();

    const { width, height } = this.layout;

    // Upper dusk wash (a calmer tint of the app bg).
    g.fillStyle(COLORS.surface, 0.35);
    g.fillRect(0, 0, width, height * 0.58);

    // Warm floor band along the bottom — the cozy hearth feeling.
    g.fillStyle(COLORS.surfaceAlt, 0.55);
    g.fillRect(0, height * 0.58, width, height * 0.42);
    g.fillStyle(COLORS.warm, 0.05);
    g.fillRect(0, height * 0.58, width, height * 0.42);

    // A faint rounded counter across the middle the two traders lean over.
    const counterH = 14;
    const counterY = height * 0.58 - counterH / 2;
    g.fillStyle(COLORS.border, 0.5);
    g.fillRoundedRect(width * 0.06, counterY, width * 0.88, counterH, counterH / 2);
  }

  // ── trade resolution (who, and what they offer) ───────────────────────────────

  /**
   * Choose the partner + the species they offer. Prefers a regular and a species the
   * player lacks; among those, prefers a *trade-evolution* species so the showcase
   * moment fires. Returns null only when nothing tradeable can be found.
   */
  private resolveTrade(trainerId?: string): { partner: SimTrainer; species: SpeciesData } | null {
    const society = this.session.society;
    if (society.length === 0) return null;

    // Candidate partners: the named one first, then regulars, then anyone.
    const named = trainerId ? society.find((t) => t.id === trainerId) : undefined;
    const regulars = this.session.regulars();
    const ordered: SimTrainer[] = [];
    const seen = new Set<string>();
    for (const t of [named, ...regulars, ...society]) {
      if (t && !seen.has(t.id)) {
        seen.add(t.id);
        ordered.push(t);
      }
    }

    // First pass: a partner who can offer a trade-evolution species the player lacks
    // (the juicy path). Second pass: any new-to-the-dex species. We pick the partner
    // from whichever pass succeeds, so the offer always feels generous + dex-useful.
    const evoPick = this.findOffer(ordered, true);
    if (evoPick) return evoPick;
    const anyPick = this.findOffer(ordered, false);
    if (anyPick) return anyPick;

    // Last resort: the named/first partner offers a hand-picked trade-evo species the
    // player lacks, even if it isn't on their roster — a regular can always "know a
    // guy". This guarantees a warm, dex-useful first trade is always possible.
    const partner = named ?? ordered[0];
    if (!partner) return null;
    const fallbackSpecies = this.fallbackOfferSpecies();
    if (!fallbackSpecies) return null;
    return { partner, species: fallbackSpecies };
  }

  /** Scan partners for a species the player lacks (optionally trade-evo only). */
  private findOffer(
    partners: readonly SimTrainer[],
    tradeEvoOnly: boolean,
  ): { partner: SimTrainer; species: SpeciesData } | null {
    for (const partner of partners) {
      for (const mon of partner.team) {
        const species = this.index.speciesById(mon.speciesId);
        if (!species) continue;
        if (this.session.hasCaught(species.id)) continue;
        if (tradeEvoOnly && !speciesHasTradeEvo(species)) continue;
        return { partner, species };
      }
    }
    return null;
  }

  /**
   * A preferred trade-evolution species the player doesn't yet have, drawn from the
   * full dataset (so even a small roster still showcases the trade-evo moment). Falls
   * back to any uncaught species, then to anything at all.
   */
  private fallbackOfferSpecies(): SpeciesData | null {
    const all = this.allSpecies();
    const lacked = all.filter((s) => !this.session.hasCaught(s.id));
    const evo = lacked.find(speciesHasTradeEvo);
    if (evo) return evo;
    if (lacked.length > 0) return lacked[0]!;
    return all[0] ?? null;
  }

  private allSpecies(): readonly SpeciesData[] {
    const bundle = this.session.bundle;
    if (bundle.species.length > 0) return bundle.species;
    return FIXTURE_SPECIES;
  }

  /** A gentle default mon to offer: the highest-level non-starter, else slot-last. */
  private pickDefaultOffer(): string | null {
    const team = this.playerTeam;
    if (team.length === 0) return null;
    const starterId = this.session.getTeam()[0]?.speciesId;
    // Prefer something that isn't the player's beloved first partner; if the team is
    // just the starter, that's fine too — a trade is always the player's choice.
    const notStarter = team.filter((m) => m.speciesId !== starterId);
    const pool = notStarter.length > 0 ? notStarter : team;
    const best = pool.reduce((a, b) => (b.level > a.level ? b : a));
    return best.id;
  }

  // ── UI construction ──────────────────────────────────────────────────────────

  private buildUi(): void {
    this.heading = this.add
      .text(0, 0, `${this.partner.username} wants to trade!`, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // ── "They offer" panel ──
    this.theirPanel = new Panel(this, 0, 0, {
      width: 168,
      height: 188,
      fill: COLORS.surface,
      shadow: true,
    });
    this.glow = this.add.image(0, 0, '__WHITE').setVisible(false);
    this.theirSprite = this.add.image(0, 0, '__MISSING').setVisible(false);
    this.theirName = this.add
      .text(0, 0, this.offeredSpecies.name, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.md}px`,
        color: CSS.accent,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const evoNote = speciesHasTradeEvo(this.offeredSpecies)
      ? 'new to your dex • might surprise you'
      : 'new to your dex';
    this.theirNote = this.add
      .text(0, 0, evoNote, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5);

    void this.loadOfferedSprite();

    // ── "You offer" side: tappable team cards ──
    this.yourLabel = this.add
      .text(0, 0, 'Send one of yours along', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5);
    this.buildOfferCards();

    // ── actions ──
    this.confirmButton = new Button(this, 0, 0, {
      label: 'Trade!',
      variant: 'primary',
      width: 200,
      reducedMotion: this.reduced,
      onClick: () => void this.doTrade(),
    });
    this.laterButton = new Button(this, 0, 0, {
      label: 'Maybe later',
      variant: 'ghost',
      width: 200,
      reducedMotion: this.reduced,
      onClick: () => this.leave(),
    });

    // ── the regular's warm greeting (bottom dialog) ──
    // Fit the greeting box to the safe width so it never overruns the screen edges on a
    // ~390px phone (a fixed 360px box overflowed once insets ate into the width).
    const dialogW = Math.min(420, this.layout.safe.width - SPACING.md * 2);
    this.dialog = new DialogBox(this, 0, 0, {
      width: dialogW,
      height: 96,
      reducedMotion: this.reduced,
    });
    this.dialog.show(this.greeting(), this.partner.username);

    this.refreshOfferSelection();
  }

  private buildOfferCards(): void {
    for (const mon of this.playerTeam) {
      const species = this.index.speciesById(mon.speciesId);
      const container = this.add.container(0, 0);
      const panel = new Panel(this, 0, 0, { width: CARD_W, height: CARD_H, fill: COLORS.surface });
      const sprite = this.add.image(0, -14, '__MISSING').setVisible(false);
      const label = mon.nickname || species?.name || mon.speciesId;
      const name = this.add
        .text(0, CARD_H / 2 - 34, label, {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.sm}px`,
          color: CSS.text,
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      const lvl = this.add
        .text(0, CARD_H / 2 - 16, `Lv ${mon.level}`, {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.xs}px`,
          color: CSS.textMuted,
        })
        .setOrigin(0.5);
      const check = this.add
        .text(CARD_W / 2 - SPACING.sm, -CARD_H / 2 + SPACING.sm, '✓', {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.md}px`,
          color: CSS.accent,
          fontStyle: 'bold',
        })
        .setOrigin(1, 0)
        .setVisible(false);

      container.add([panel, sprite, name, lvl, check]);
      container.setSize(CARD_W, CARD_H);
      container.setInteractive(
        new Phaser.Geom.Rectangle(-CARD_W / 2, -CARD_H / 2, CARD_W, CARD_H),
        Phaser.Geom.Rectangle.Contains,
      );
      container.input!.cursor = 'pointer';
      // Select on TAP (POINTER_UP); a drag-scroll of the offer band no-ops here.
      container.on(Phaser.Input.Events.POINTER_UP, () => {
        if (!this.offerScroll?.wasDragging()) this.selectOffer(mon.id);
      });

      this.offerCards.push({ mon, container, panel, sprite, check });
      if (species) void this.loadCardSprite(species, sprite);
    }
  }

  private selectOffer(id: string): void {
    if (this.busy) return;
    this.chosenOfferId = id;
    this.refreshOfferSelection();
  }

  private refreshOfferSelection(): void {
    for (const card of this.offerCards) {
      const selected = card.mon.id === this.chosenOfferId;
      card.container.setScale(selected ? 1.05 : 1);
      card.container.setAlpha(selected ? 1 : 0.72);
      card.check.setVisible(selected);
    }
    this.confirmButton?.setDisabled(this.chosenOfferId == null);
  }

  // ── sprite loading (all guarded; never crash on a miss) ───────────────────────

  private async loadOfferedSprite(): Promise<void> {
    const resolved = await this.resolver.battle(this.offeredSpecies.dexNo, 'front');
    if (!resolved || !this.scene.isActive() || !this.theirSprite) return;
    this.theirSprite.setTexture(resolved.key);
    fitSprite(this.theirSprite, 112);
    this.theirSprite.setPosition(this.theirSpriteAnchor.x, this.theirSpriteAnchor.y);
    this.theirSprite.setVisible(true);
  }

  private async loadCardSprite(
    species: SpeciesData,
    sprite: Phaser.GameObjects.Image,
  ): Promise<void> {
    const resolved = await this.resolver.battle(species.dexNo, 'front');
    if (!resolved || !this.scene.isActive()) return;
    sprite.setTexture(resolved.key);
    fitSprite(sprite, OFFER_ART);
    sprite.setVisible(true);
  }

  // ── the trade itself ──────────────────────────────────────────────────────────

  private async doTrade(): Promise<void> {
    if (this.busy || this.committed || this.chosenOfferId == null) return;
    this.busy = true;
    this.confirmButton?.setDisabled(true);
    this.laterButton?.setDisabled(true);

    const offered = this.playerTeam.find((m) => m.id === this.chosenOfferId);
    if (!offered) {
      this.busy = false;
      this.refreshOfferSelection();
      return;
    }

    // Build the received mon as a fully-formed, battle-ready, player-owned instance.
    const received = this.buildReceivedInstance(this.offeredSpecies);

    // The trade-evolution canon (core/evolution): does this mon evolve ON the trade?
    const evoMatch = checkEvolution(received, this.offeredSpecies, 'trade');
    const evolvedSpecies = evoMatch ? (this.index.speciesById(evoMatch.toSpeciesId) ?? null) : null;

    await this.playSwapAnimation();
    if (!this.scene.isActive()) return;

    let finalMon = received;
    if (evoMatch && evolvedSpecies) {
      finalMon = evolveInstance(received, evolvedSpecies);
      await this.playEvolutionMoment(this.offeredSpecies, evolvedSpecies);
      if (!this.scene.isActive()) return;
    }

    await this.commitTrade(offered, finalMon);

    const arrivedName = finalMon.nickname || speciesNameOf(finalMon, this.index);
    const closing =
      evoMatch && evolvedSpecies
        ? `${arrivedName}! It evolved the moment it arrived — lucky you. Take good care of it!`
        : `${arrivedName} is yours now. Thanks for the trade — come find me anytime!`;
    await this.speak(closing);

    this.spawnToast(`${arrivedName} joined you — added to your dex!`);
    this.committed = true;
    this.time.delayedCall(this.reduced ? 200 : 900, () => this.leave());
  }

  /**
   * Compose the received mon as a real, battle-ready owned instance: real stats via
   * `computeStats` (core owns the formula — the renderer only calls it), real PP from
   * the move data, full HP, neutral nature, and `viaTrade: true` so the world treats
   * it warmly. Identity is anchored deterministically to the world + partner so a
   * replayed session is reproducible.
   */
  private buildReceivedInstance(species: SpeciesData): PokemonInstance {
    const rng = this.session.expeditionRng(`trade-${this.partner.id}-${species.id}`);
    const level = Math.max(5, this.medianTeamLevel());
    const moves = this.movesFor(species, level);

    const ivs = { hp: 20, atk: 20, def: 20, spatk: 20, spdef: 20, spe: 20 };
    const evs = { hp: 0, atk: 0, def: 0, spatk: 0, spdef: 0, spe: 0 };
    const nature = getNature('hardy');
    const stats = computeStats(species.baseStats, ivs, evs, level, nature);

    return {
      id: `player-trade-${species.id}-${rng.getState().toString(36)}`,
      speciesId: species.id,
      nickname: null,
      level,
      exp: expForLevel(species.growthRate, level),
      natureId: nature.id,
      ivs,
      evs,
      abilityId: species.abilities.primary,
      moves,
      currentHp: stats.hp,
      maxHp: stats.hp,
      status: 'none',
      statusTurns: 0,
      heldItemId: null,
      friendship: 70,
      shiny: false,
      gender:
        species.genderRatio == null ? null : rng.float() < species.genderRatio ? 'female' : 'male',
      // Marked as a trade so the world (and EXP bonus, later) treats it warmly.
      originalTrainer: {
        trainerId: this.partner.id,
        trainerName: this.partner.username,
        viaTrade: true,
      },
      obtainedAt: Date.now(),
    };
  }

  private movesFor(species: SpeciesData, level: number): MoveSlot[] {
    const learnable = species.learnset
      .filter((e) => e.method === 'level-up' && (e.level ?? 1) <= level)
      .sort((a, b) => (a.level ?? 1) - (b.level ?? 1))
      .slice(-4);
    const list = learnable.length > 0 ? learnable : species.learnset.slice(0, 1);
    const slots: MoveSlot[] = [];
    for (const e of list) {
      const md: MoveData | undefined = this.index.moveById(e.moveId);
      const maxPp = md?.pp ?? 5;
      slots.push({ moveId: e.moveId, pp: maxPp, maxPp });
    }
    if (slots.length === 0) slots.push({ moveId: 'tackle', pp: 35, maxPp: 35 });
    return slots;
  }

  private medianTeamLevel(): number {
    const levels = this.playerTeam.map((m) => m.level).sort((a, b) => a - b);
    if (levels.length === 0) return 5;
    return levels[Math.floor(levels.length / 2)]!;
  }

  /**
   * Persist the swap through the GameSession (the only persistence owner). We rebuild
   * the immutable Save once: remove the offered mon from the player's team/boxes, drop
   * in the received (possibly evolved) mon, mark the dex, and hand the offered mon to
   * the partner with a warm bump in friendship + a memory. The session then reloads +
   * saves. Rules stayed in core/evolution + core/stats; this is pure orchestration +
   * persistence.
   */
  private async commitTrade(offered: PokemonInstance, received: PokemonInstance): Promise<void> {
    const save = this.session.toSave();

    // Remove the offered mon from team + every box (it's leaving the player).
    const team = save.team.filter((m) => m.id !== offered.id);
    const boxes = save.boxes.map((b) => ({
      ...b,
      contents: b.contents.filter((m) => m.id !== offered.id),
    }));

    // Place the received mon: team if there's room, else the first box.
    if (team.length < 6) {
      team.push(received);
    } else {
      const box = boxes[0] ?? { id: 'box-1', name: 'Box 1', contents: [] };
      box.contents = [...box.contents, received];
      if (!boxes.includes(box)) boxes.push(box);
    }

    // Dex: the received species is now caught + seen.
    const seen = dedupe([...save.dex.seen, received.speciesId, this.offeredSpecies.id]);
    const caught = dedupe([...save.dex.caught, received.speciesId]);

    // The partner receives the player's mon + a warm memory + a friendship bump.
    const offeredName = speciesNameOf(offered, this.index);
    const givenToPartner: PokemonInstance = {
      ...offered,
      originalTrainer: { ...offered.originalTrainer }, // keep its origin trail
    };
    const trainers = save.society.trainers.map((t) => {
      if (t.id !== this.partner.id) return t;
      const memory = {
        id: `mem-trade-${t.id}-${received.speciesId}-${save.lastPlayedAt}`,
        aboutId: 'player',
        kind: 'traded',
        summary: `traded you a ${this.offeredSpecies.name} for your ${offeredName}`,
        at: Date.now(),
      };
      return {
        ...t,
        team: [...t.team, givenToPartner],
        friendshipWithPlayer: Math.min(100, t.friendshipWithPlayer + 8),
        memories: [...t.memories, memory].slice(-12),
      };
    });

    const nextSave = {
      ...save,
      team,
      boxes,
      dex: { seen, caught },
      society: { ...save.society, trainers },
    };

    try {
      // importFromString rebuilds the live working copy from the (current-version)
      // save we just assembled; save() then persists it to IndexedDB.
      this.session.importFromString(JSON.stringify(nextSave));
      await this.session.save();
    } catch {
      // A storage / round-trip hiccup must never crash a cozy moment. The player still
      // saw the trade; the next autosave will retry from the live world.
    }
  }

  // ── the animated moments ──────────────────────────────────────────────────────

  /** A calm cross of the two sprites — the classic "swap" beat, gentle + brief. */
  private playSwapAnimation(): Promise<void> {
    return new Promise((resolve) => {
      const their = this.theirSprite;
      if (this.reduced || !their) {
        resolve();
        return;
      }
      void this.speak('Here it comes…');
      this.tweens.add({
        targets: their,
        y: their.y - 18,
        duration: 360,
        yoyo: true,
        repeat: 1,
        ease: 'Sine.easeInOut',
        onComplete: () => resolve(),
      });
    });
  }

  /** The juicy evolution moment: a soft white bloom + a happy line. */
  private playEvolutionMoment(from: SpeciesData, to: SpeciesData): Promise<void> {
    return new Promise((resolve) => {
      const their = this.theirSprite;
      const glow = this.glow;

      const finish = async (): Promise<void> => {
        // Swap the displayed sprite to the evolved form, then resolve.
        const resolvedSprite = await this.resolver.battle(to.dexNo, 'front');
        if (this.scene.isActive() && their && resolvedSprite) {
          their.setTexture(resolvedSprite.key);
          fitSprite(their, 112);
          their.setPosition(this.theirSpriteAnchor.x, this.theirSpriteAnchor.y);
        }
        if (this.scene.isActive() && this.theirName) {
          this.theirName.setText(to.name);
        }
        resolve();
      };

      if (this.reduced || !their || !glow) {
        void finish();
        return;
      }

      void this.speak(`Wait — what's happening to the ${from.name}?!`);
      glow.setVisible(true).setAlpha(0).setTint(0xffffff);
      glow.setPosition(their.x, their.y);
      glow.setDisplaySize(140, 140);

      this.tweens.add({
        targets: glow,
        alpha: 0.95,
        duration: 520,
        ease: 'Sine.easeIn',
        onComplete: () => {
          this.tweens.add({
            targets: their,
            scaleX: their.scaleX * 1.12,
            scaleY: their.scaleY * 1.12,
            duration: 320,
            yoyo: true,
            ease: 'Sine.easeInOut',
          });
          this.tweens.add({
            targets: glow,
            alpha: 0,
            delay: 320,
            duration: 520,
            ease: 'Sine.easeOut',
            onComplete: () => {
              glow.setVisible(false);
              void finish();
            },
          });
        },
      });
    });
  }

  // ── small helpers ─────────────────────────────────────────────────────────────

  private greeting(): string[] {
    const evo = speciesHasTradeEvo(this.offeredSpecies);
    const a = `Oh, ${this.session.trainerName}! Perfect timing — I've got a spare ${this.offeredSpecies.name} looking for a good home.`;
    const b = evo
      ? `Word is they do something special when they change hands… want to swap? No pressure at all.`
      : `Trade me one of yours and it's all yours — great for your dex. Take your time choosing.`;
    return [a, b];
  }

  /** Show a one-off line in the dialog box (replaces whatever is there). */
  private speak(line: string): Promise<void> {
    if (!this.dialog) return Promise.resolve();
    this.dialog.show(line, this.partner.username);
    // Give a brief calm beat for the line to read; never block on a tap here.
    return new Promise((resolve) => {
      const ms = this.reduced ? 150 : 700;
      this.time.delayedCall(ms, () => resolve());
    });
  }

  private spawnToast(text: string): void {
    const p = this.layout.place('top', { width: 0, height: 0 }, SPACING.xl);
    new Toast(this, p.x, p.y + SPACING.xl, {
      text,
      accent: COLORS.warm,
      reducedMotion: this.reduced,
    });
  }

  private showNothingToTrade(): void {
    this.heading = this.add
      .text(0, 0, 'No trades just now', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const sub = this.add
      .text(0, 0, 'Everyone here is happy with their team today.\nCome back another time!', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
        align: 'center',
      })
      .setOrigin(0.5);
    this.theirNote = sub;
    this.laterButton = new Button(this, 0, 0, {
      label: 'Back to town',
      variant: 'primary',
      width: 220,
      reducedMotion: this.reduced,
      onClick: () => this.leave(),
    });
  }

  private leave(): void {
    if (this.committed === false && this.busy) return; // mid-trade: ignore stray taps
    goTo(this, 'Town');
  }

  // ── layout (responsive: side-by-side on desktop, stacked on phone) ────────────

  private relayout(): void {
    this.drawBackdrop();
    const safe = this.layout.safe;
    const cx = this.layout.centerX;
    const top = safe.y + SPACING.xl;

    this.heading?.setPosition(cx, top);

    if (!this.theirPanel) {
      // The "nothing to trade" variant: center the message + button.
      if (this.theirNote) this.theirNote.setPosition(cx, this.layout.centerY - SPACING.lg);
      this.laterButton?.setPosition(cx, this.layout.centerY + SPACING.xxl);
      return;
    }

    const stacked = safe.width < 520;
    const sectionTop = top + SPACING.xl + SPACING.md;

    // ── "They offer" panel ──
    let theirCx: number;
    let theirCy: number;
    if (stacked) {
      theirCx = cx;
      theirCy = sectionTop + 94;
    } else {
      theirCx = cx - safe.width / 4;
      theirCy = sectionTop + 110;
    }
    this.theirPanel.setPosition(theirCx, theirCy);
    this.theirName?.setPosition(theirCx, theirCy + 56);
    this.theirNote?.setPosition(theirCx, theirCy + 78);
    this.theirSpriteAnchor = { x: theirCx, y: theirCy - 18 };
    this.theirSprite?.setPosition(theirCx, theirCy - 18);
    this.glow?.setPosition(theirCx, theirCy - 18);

    // ── actions + dialog pinned to the bottom safe edge (computed first so the offer
    //    cards know the bottom bound of their band and never collide with them) ──
    const dialogH = 96; // fixed at construction
    const dialogCy = safe.y + safe.height - SPACING.md - dialogH / 2;
    this.dialog?.setPosition(cx, dialogCy);

    const btnY = dialogCy - dialogH / 2 - SPACING.lg;
    let actionsTop: number;
    if (stacked) {
      const confirmY = btnY - SPACING.xl - 8;
      this.confirmButton?.setPosition(cx, confirmY);
      this.laterButton?.setPosition(cx, btnY);
      actionsTop = confirmY - (this.confirmButton?.buttonHeight ?? 48) / 2;
    } else {
      this.confirmButton?.setPosition(cx - 110, btnY);
      this.laterButton?.setPosition(cx + 110, btnY);
      actionsTop = btnY - (this.confirmButton?.buttonHeight ?? 48) / 2;
    }

    // ── "You offer" cards ──
    let cardsTop: number;
    let cardsCx: number;
    if (stacked) {
      this.yourLabel?.setPosition(cx, theirCy + 110);
      cardsTop = theirCy + 134;
      cardsCx = cx;
    } else {
      cardsCx = cx + safe.width / 4;
      this.yourLabel?.setPosition(cardsCx, sectionTop - SPACING.xs);
      cardsTop = sectionTop + 28;
    }
    // The cards may only occupy the band between their top and the action buttons; on a
    // phone with a full team that band is shorter than the card column, so they scroll.
    const cardsBottom = actionsTop - SPACING.md;
    this.layoutOfferCards(cardsCx, cardsTop, cardsBottom, safe.width, stacked);
  }

  private layoutOfferCards(
    cx: number,
    top: number,
    bottom: number,
    safeW: number,
    stacked: boolean,
  ): void {
    if (this.offerCards.length === 0) return;
    const gap = SPACING.sm;
    const available = stacked ? safeW - SPACING.lg : safeW / 2 - SPACING.lg;
    const perRow = Math.max(
      1,
      Math.min(this.offerCards.length, Math.floor(available / (CARD_W + gap))),
    );
    const cols = Math.max(1, perRow);
    const rows = Math.ceil(this.offerCards.length / cols);
    const rowW = cols * CARD_W + (cols - 1) * gap;
    const gridH = rows * CARD_H + (rows - 1) * gap;
    const bandH = Math.max(CARD_H, bottom - top);

    // Fits the band → place the cards directly (tear down any prior scroll list).
    if (gridH <= bandH) {
      if (this.offerScroll) {
        for (const card of this.offerCards) {
          if (card.container.parentContainer === this.offerScroll.content) {
            this.offerScroll.content.remove(card.container);
            this.add.existing(card.container);
          }
        }
        this.offerScroll.destroy();
        this.offerScroll = undefined;
      }
      const startX = cx - rowW / 2 + CARD_W / 2;
      this.offerCards.forEach((card, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        card.container.setPosition(
          startX + col * (CARD_W + gap),
          top + CARD_H / 2 + row * (CARD_H + gap),
        );
      });
      return;
    }

    // Overflows the band → scroll. The cards live in the list's content (centered on cx,
    // laid out top-down from y=0), so every team mon stays reachable by touch-drag.
    const listW = rowW + SPACING.md;
    if (!this.offerScroll) {
      this.offerScroll = new ScrollList(this, cx, top + bandH / 2, { width: listW, height: bandH });
    } else {
      this.offerScroll.setPosition(cx, top + bandH / 2);
      this.offerScroll.resize(listW, bandH);
    }
    const list = this.offerScroll;
    const startX = -rowW / 2 + CARD_W / 2;
    const topPad = SPACING.xs;
    this.offerCards.forEach((card, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      if (card.container.parentContainer !== list.content) list.content.add(card.container);
      card.container.setPosition(
        startX + col * (CARD_W + gap),
        topPad + CARD_H / 2 + row * (CARD_H + gap),
      );
    });
    list.setContentHeight(topPad + gridH + SPACING.xs);
    list.scrollTo(0);
  }
}

// ── module helpers (pure) ────────────────────────────────────────────────────────

/** Does this species have a `trade`-triggered evolution branch? */
function speciesHasTradeEvo(species: SpeciesData): boolean {
  return species.evolutions.some((e) => e.trigger === 'trade');
}

/** Resolve a mon's display species name (falls back to its id). */
function speciesNameOf(mon: PokemonInstance, index: DataIndex): string {
  return index.speciesById(mon.speciesId)?.name ?? mon.speciesId;
}

/** Scale an image to fit a square `maxSide` without distortion (capped upscale). */
function fitSprite(sprite: Phaser.GameObjects.Image, maxSide: number): void {
  const w = sprite.width || 1;
  const h = sprite.height || 1;
  const scale = Math.min(maxSide / w, maxSide / h, 3);
  sprite.setScale(scale);
}

function dedupe(ids: readonly string[]): string[] {
  return [...new Set(ids.filter((id) => !!id))];
}

/** Fixtures-backed index when the registry has none (defensive parity with NewGame). */
function fixtureIndex(): DataIndex {
  const byId = new Map(FIXTURE_SPECIES.map((s) => [s.id, s]));
  const byDex = new Map(FIXTURE_SPECIES.map((s) => [s.dexNo, s]));
  return {
    speciesById: (id) => byId.get(id),
    speciesByDexNo: (dexNo) => byDex.get(dexNo),
    moveById: () => undefined,
  };
}
